// Every element in the model, with what the field has done to it.
//
// The model's own hierarchy - category, then family, then type, then the
// elements - because that is the one a person recognises when they are
// looking at the thing in front of them.
//
// LOADED A LEVEL AT A TIME. A type node here can hold forty thousand
// elements; a tree that ships them all is the mistake that made the
// Project Files tree parse 300 MB before the model would open. Opening a
// node fetches that node.
//
// Two gestures, deliberately different. The toggle in front of a row
// moves that row on one step, which is what the field does all day and
// should cost one tap. The status menu on a section sets a whole branch,
// which is what a foreman does at the end of a day and should be
// deliberate enough to be read first.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dropdown, Empty, Input, Menu, Space, Spin, Tag, Tooltip, Tree, message } from "antd";
import { popupInStage } from "./stagePopup";
import selectDerivedParts from "./selectDerivedParts";
import { CheckCircleOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { getConduitSizeUids, getElementTree, setElementStatus } from "api/tracking/trackingElementsApi";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { dateForStatus } from "./stageHelpers";
import { announceStatus, setStatusWithPreview } from "./statusBulk";
import { DEFAULT_UNITS, SUM_COLUMNS, buildNote, compact, exact, unitOf } from "./quantityFormat";

// Width of one amount column, and the gap in front of the status menu.
const COL_W = 62;

const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB" };

const nextStatus = (s) => STATUS_ORDER[(Math.max(0, STATUS_ORDER.indexOf(s)) + 1) % STATUS_ORDER.length];

// A dot that IS the control. Colour says where the element is, clicking
// moves it on one step, and the tooltip names both so the colour never
// has to be learned from a key.
function StatusDot({ status, onSet, disabled }) {
  const s = status || "projected";
  return (
    <Tooltip getPopupContainer={popupInStage} title={`${STATUS_LABEL[s] || s} - click for ${STATUS_LABEL[nextStatus(s)]}`}>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); if (!disabled) onSet(nextStatus(s)); }}
        onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { e.stopPropagation(); onSet(nextStatus(s)); } }}
        style={{
          display: "inline-block", width: 12, height: 12, borderRadius: 3,
          background: STATUS_COLOR[s], marginRight: 8, flexShrink: 0,
          cursor: disabled ? "default" : "pointer",
          outline: "none", border: "1px solid rgba(0,0,0,.15)",
        }}
      />
    </Tooltip>
  );
}

export default function ElementTree({
  projectId,
  documentIds = [],
  canEdit = true,
  checkedKeys = [],
  onCheck,
  onStatusChanged,
  onSelectElement,
  // Selecting a CUSTOM CATEGORY in the model: the same (uids, name,
  // definitionId) callback the custom category list uses, so the page has
  // one notion of what is picked.
  onSelectParts = null,
  // WHICH CUSTOM CATEGORIES ARE TICKED, emitted upward so the page saves
  // them with the scope. They are kept out of `checkedKeys` on purpose:
  // that array is a set of model node keys and it is handed to the
  // viewer's scope filter, the preview counts and the saved scope, none
  // of which can resolve a custom category. A tick that travelled with
  // them would look saved and quietly select nothing.
  onDerivedTicks = null,
  // The page's ticked keys, replaced outright - for the Conduits by size /
  // Junction boxes / Pull boxes entries, whose rows are not the ant Tree's.
  onSetChecked = null,
  statusByUid,
  // The date a status change is recorded at (YYYY-MM-DD). The field
  // marks work that happened last week as often as work happening now.
  statusDate = "",
  projectedDate = "",
  height = 460,
  // THE PAGE'S LEVELS (owner, 2026-09-22): the tree counts - and lists - only
  // the elements on the levels the page is set to. Empty = every level.
  pageLevels = [],
  // The elevation half of the page cut, as a selection id the server
  // resolves (utils/pageCut). It travels with the levels everywhere,
  // because an element in the cut by uid is one whose level NAME cannot
  // select it - see the comment on cutUidsId in ModelTracking.
  cutUids = "",
}) {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [filter, setFilter] = useState("");
  const [busyKey, setBusyKey] = useState("");
  // Amounts read from the record files: build state + units, from the
  // same tree responses.
  const [quantities, setQuantities] = useState(null);
  // The custom categories, kept APART from `nodes`. They count parts, not
  // elements, so they are a second axis over the same model rather than
  // more of the first - see the tree route.
  const [derived, setDerived] = useState([]);
  const [derivedCut, setDerivedCut] = useState(false);
  const [busyDerived, setBusyDerived] = useState("");
  // Seeded from the server's `tracked` flag on each load, which reads the
  // saved scope. An empty saved scope means nothing has been chosen yet,
  // which on this page means everything - so the server sends tracked:true
  // and the boxes arrive ticked rather than all off.
  const [derivedTicks, setDerivedTicks] = useState([]);
  // SIZES AND BOXES AS TOP ENTRIES (owner, 2026-10-19). From the root
  // answer's `shortcuts`; the conduit sizes' element ids load on first use.
  const [shortcuts, setShortcuts] = useState([]);
  const [sizeUids, setSizeUids] = useState(null);
  const [sizeBusy, setSizeBusy] = useState(false);
  const units = (quantities && quantities.units) || DEFAULT_UNITS;

  const docs = useMemo(() => documentIds.join(","), [documentIds]);
  // '|'-joined: level names carry commas ("CUP - Level 1 = 97'-6\"").
  const lv = useMemo(() => pageLevels.join("|"), [pageLevels]);
  const withLv = (p) => ({ ...p, ...(lv ? { pageLevels: lv } : {}), ...(cutUids ? { cutUids } : {}) });

  // Root level.
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    setLoading(true);
    setExpanded([]);
    getElementTree(projectId, withLv({ documentIds: docs }))
      .then((r) => {
        if (!alive) return;
        setQuantities(r.quantities || null);
        setDerived((r.derived || []).map((d) => ({
          key: d.key, title: d.label, count: d.count, level: "derived",
          derivedId: d.derivedId, sourceCategory: d.sourceCategory || "",
          parentRows: d.parentRows || 0, measures: d.measures || [], amounts: d.amounts || {},
          without: d.without || null, isLeaf: !d.hasChildren,
        })));
        setDerivedCut(!!r.derivedIgnoresPageCut);
        setDerivedTicks((r.derived || []).filter((d) => d.tracked !== false).map((d) => d.derivedId));
        setShortcuts(r.shortcuts || []);
        setSizeUids(null);
        // OPEN, NOT COLLAPSED. The whole complaint was that these were
        // nowhere to be seen; a heading that has to be found and clicked
        // is the same problem one step smaller. The model's own
        // categories stay closed because there are dozens of them and
        // each holds thousands of rows - there are three of these.
        setExpanded((r.derived || []).length ? ["derivedRoot"] : []);
        setNodes((r.nodes || []).map((n) => ({
          key: n.key, title: n.label, count: n.count, isLeaf: false, level: "category",
          category: n.value, q: n.q || null,
        })));
      })
      .catch((e) => { if (alive) message.error(e.message || "Could not read the model tree"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, docs, lv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!projectId || !quantities || quantities.state === "done" || quantities.state === "none" || quantities.state === "failed") return undefined;
    const t = setTimeout(() => {
      getElementTree(projectId, withLv({ documentIds: docs }))
        .then((r) => {
          setQuantities(r.quantities || null);
          if (r.quantities && r.quantities.state === "done") {
            const byKey = new Map((r.nodes || []).map((n) => [n.key, n.q || null]));
            // Fresh sums on the categories; opened branches refill on reopen.
            setNodes((prev) => prev.map((n) => ({ ...n, q: byKey.get(n.key) || n.q, children: undefined })));
            // Opened branches refill on reopen - but the custom categories
            // heading is not one of the model's branches and closing it
            // here would hide them again the moment a build finished.
            setExpanded((prev) => (prev.includes("derivedRoot") ? ["derivedRoot"] : []));
          }
        })
        .catch(() => {});
    }, 15000);
    return () => clearTimeout(t);
  }, [projectId, docs, quantities, lv]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChildren = useCallback(async (node) => {
    // A CUSTOM CATEGORY opens into its part groups, which are leaves: the
    // part family and type, how many there are, and the amounts they
    // carry. They are not tickable and not status rows - see the note
    // under the tree.
    if (node.level === "derived") {
      const r = await getElementTree(projectId, { documentIds: docs, derivedId: node.derivedId });
      const groups = (r.groups || []).map((g) => ({
        key: g.key, title: g.label, count: g.totalCount, isLeaf: true, level: "derivedPart",
        measures: g.measures || [], amounts: g.amounts || {}, without: g.without || null,
        parentCount: g.parentCount || 0, perParent: g.perParent,
      }));
      // The server caps this list and says when it did. A capped list that
      // reads as the whole of something is the mistake this names.
      if (r.capped) {
        groups.push({
          key: `${node.key}|capped`, isLeaf: true, level: "note",
          title: `Only the largest ${groups.length.toLocaleString()} part groups are listed`,
        });
      }
      return groups;
    }
    const params = { documentIds: docs };
    if (node.level === "category") params.category = node.category;
    if (node.level === "family") { params.category = node.category; params.family = node.family; }
    if (node.level === "type") {
      params.category = node.category; params.family = node.family; params.typeName = node.typeName;
      params.limit = 500;
    }
    const r = await getElementTree(projectId, withLv(params));
    if (node.level === "type") {
      return (r.elements || []).map((e) => ({
        key: e.key, title: e.name || e.typeName || e.uniqueId.slice(0, 8),
        isLeaf: true, level: "element", uniqueId: e.uniqueId, status: e.status,
        category: node.category, family: node.family, typeName: node.typeName, q: e.q || null,
      }));
    }
    return (r.nodes || []).map((n) => ({
      key: n.key, title: n.label, count: n.count, isLeaf: false, q: n.q || null,
      level: node.level === "category" ? "family" : "type",
      category: node.category,
      family: node.level === "category" ? n.value : node.family,
      typeName: node.level === "family" ? n.value : undefined,
    }));
  }, [projectId, docs, lv]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadData = useCallback(async (treeNode) => {
    const node = treeNode.__node;
    if (!node || node.children) return;
    if (node.level === "derivedRoot" || node.level === "shortcut") return;
    try {
      const children = await loadChildren(node);
      // A custom category's children belong to the custom categories, not
      // to the model's own nodes: one list per axis, so neither can
      // overwrite the other's rows.
      if (node.level === "derived") {
        setDerived((prev) => prev.map((d) => (d.key === node.key ? { ...d, children } : d)));
        return;
      }
      setNodes((prev) => {
        const clone = (list) => list.map((n) => (
          n.key === node.key ? { ...n, children } : (n.children ? { ...n, children: clone(n.children) } : n)
        ));
        return clone(prev);
      });
    } catch (e) {
      message.error(e.message || "Could not open that node");
    }
  }, [loadChildren]);

  // A status change on a row or a branch. The branch case sends the NODE
  // KEY rather than the ids under it: the server resolves it, so opening
  // a forty-thousand-element type is not a precondition for marking it.
  const applyStatus = useCallback(async (node, status) => {
    if (!canEdit) return;
    setBusyKey(node.key);
    try {
      const body = node.level === "element"
        ? { uniqueIds: [node.uniqueId], status, date: dateForStatus(status, statusDate, projectedDate) }
        // A node's status on a page set to levels marks that node ON those levels.
        : { nodeKeys: [node.key], nodeAct: true, status, documentIds: docs, date: dateForStatus(status, statusDate, projectedDate), ...(pageLevels.length ? { pageLevels } : {}), ...(cutUids ? { cutUids } : {}) };
      // A branch says how many it will change first (owner, 2026-09-18).
      const r = node.level === "element" ? await setElementStatus(projectId, body) : await setStatusWithPreview(projectId, body);
      if (!r) return;
      announceStatus(projectId, r, status, () => { if (onStatusChanged) onStatusChanged({ node, status, count: 0, undone: true }); });
      if (node.level === "element") {
        setNodes((prev) => {
          const walk = (list) => list.map((n) => (
            n.key === node.key ? { ...n, status }
              : (n.children ? { ...n, children: walk(n.children) } : n)
          ));
          return walk(prev);
        });
      }
      if (onStatusChanged) onStatusChanged({ node, status, count: r.count });
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBusyKey("");
    }
  }, [canEdit, docs, projectId, onStatusChanged, statusDate, projectedDate, pageLevels, cutUids]);

  const statusMenu = (node) => (
    <Menu
      onClick={({ key, domEvent }) => { domEvent.stopPropagation(); applyStatus(node, key); }}
      items={STATUS_ORDER.map((s) => ({
        key: s,
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[s] }} />
            {STATUS_LABEL[s]}
          </span>
        ),
      }))}
    />
  );

  // The tree the ant component renders. The node itself rides along on
  // __node so the handlers do not have to re-derive it from the key.
  const toAnt = useCallback((list) => list
    .filter((n) => !filter || String(n.title).toLowerCase().includes(filter.toLowerCase()))
    .map((n) => ({
      key: n.key,
      isLeaf: n.isLeaf,
      __node: n,
      children: n.children ? toAnt(n.children) : undefined,
      title: (
        <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
          {n.level === "element" && (
            <StatusDot
              status={(statusByUid && statusByUid.get(n.uniqueId)) || n.status}
              disabled={!canEdit || busyKey === n.key}
              onSet={(s) => applyStatus(n, s)}
            />
          )}
          <span
            style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            onClick={() => { if (n.level === "element" && onSelectElement) onSelectElement(n.uniqueId); }}
          >
            {n.title}
          </span>
          {n.count !== undefined && (
            <Tag style={{ marginInlineEnd: 4 }} color="default">{n.count.toLocaleString()}</Tag>
          )}
          {/* The amounts, rolled up from the elements under this row. */}
          {SUM_COLUMNS.map((col) => {
            const v = n.q ? n.q[col.key] : 0;
            return (
              <Tooltip getPopupContainer={popupInStage} key={col.key} title={n.q ? `${col.label}: ${exact(v, unitOf(col, units))}${n.level !== "element" && n.q.n ? ` over ${n.q.n.toLocaleString()} element${n.q.n === 1 ? "" : "s"} with quantities` : ""}` : `${col.label}: not read yet`}>
                <span style={{ width: COL_W, flexShrink: 0, textAlign: "right", fontSize: 11.5, color: v ? T.ink : "#C0C4C9", fontVariantNumeric: "tabular-nums", paddingRight: 4 }}>
                  {n.q ? compact(v) : ""}
                </span>
              </Tooltip>
            );
          })}
          {canEdit && n.level !== "element" && (
            <Dropdown getPopupContainer={popupInStage} overlay={statusMenu(n)} trigger={["click"]}>
              <Button
                size="small" type="text" loading={busyKey === n.key}
                icon={<CheckCircleOutlined />}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 24, flexShrink: 0 }}
              />
            </Dropdown>
          )}
          {/* Keeps element rows' amounts under the section rows' columns. */}
          {canEdit && n.level === "element" && <span style={{ width: 24, flexShrink: 0 }} />}
        </span>
      ),
    })), [filter, statusByUid, canEdit, busyKey, applyStatus, onSelectElement, units]);

  const antNodes = useMemo(() => toAnt(nodes), [toAnt, nodes]);

  // The amounts of a part row, in the same columns as everything else.
  // A measure the model does not carry prints NOTHING and says so on
  // hover - never a zero, which compact() renders as an en dash and is
  // indistinguishable from missing.
  const partAmounts = useCallback((n) => SUM_COLUMNS.map((col) => {
    const has = (n.measures || []).includes(col.key);
    const v = has ? n.amounts[col.key] : null;
    return (
      <Tooltip
        getPopupContainer={popupInStage}
        key={col.key}
        title={has
          ? `${col.label}: ${exact(v, unitOf(col, units))}${n.without && n.without[col.key] ? ` - ${n.without[col.key].toLocaleString()} of these parts carry no ${col.label.toLowerCase()}` : ""}`
          : `The model carries no ${col.label.toLowerCase()} for these parts`}
      >
        <span style={{ width: COL_W, flexShrink: 0, textAlign: "right", fontSize: 11.5, color: has ? T.ink : "#C0C4C9", fontVariantNumeric: "tabular-nums", paddingRight: 4 }}>
          {has ? compact(v) : ""}
        </span>
      </Tooltip>
    );
  }), [units]);

  const toggleDerived = useCallback((id, on) => {
    setDerivedTicks((prev) => {
      const next = on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id);
      if (onDerivedTicks) onDerivedTicks(next);
      return next;
    });
  }, [onDerivedTicks]);

  const selectParts = useCallback(async (node) => {
    if (!onSelectParts) return;
    setBusyDerived(node.key);
    try {
      await selectDerivedParts({ projectId, documentIds: docs, id: node.derivedId, name: node.title, onSelect: onSelectParts });
    } catch (e) {
      message.error(e.message || "Could not read that custom category");
    } finally {
      setBusyDerived("");
    }
  }, [onSelectParts, projectId, docs]);

  // CUSTOM CATEGORIES, under their own heading.
  //
  // NOT CHECKABLE, and that is a decision rather than an omission. A tick
  // in this tree means "track this", which the server reads as a node key
  // over the model's own hierarchy; a custom category is a stored
  // definition resolved against the live commit and has no such shape, so
  // a checkbox here would either throw or quietly track nothing. And
  // status on these parts follows the PARENT element today (the server
  // states that as statusMode: 'parent'), so a status control here would
  // silently mean something different from the identical control one row
  // above. Both are said in the note under the tree instead of implied by
  // a disabled box nobody can interpret.
  const derivedTree = useMemo(() => {
    if (!derived.length) return [];
    const rowTitle = (n) => (
      <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
        {/* ITS OWN CHECKBOX, not the ant Tree's. The row is checkable:false
            to the Tree so its key can never join checkedKeys; this box is
            wired to derivedTicks, which the page saves as derivedIds. */}
        {n.level === "derived" && canEdit ? (
          <Tooltip
            getPopupContainer={popupInStage}
            title={derivedTicks.includes(n.derivedId)
              ? "Tracked - its parts are counted in the quantities table below"
              : "Not tracked - it stays here but is left out of the quantities table"}
          >
            <Checkbox
              checked={derivedTicks.includes(n.derivedId)}
              onChange={(e) => { e.stopPropagation(); toggleDerived(n.derivedId, e.target.checked); }}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 8, flexShrink: 0 }}
            />
          </Tooltip>
        ) : null}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {n.title}
          {n.level === "derived" && n.sourceCategory
            ? <span style={{ color: T.muted, fontSize: 11 }}>{` · from ${n.sourceCategory}`}</span> : null}
          {n.level === "derivedPart" && n.parentCount
            ? <span style={{ color: T.muted, fontSize: 11 }}>{` · in ${n.parentCount.toLocaleString()} element${n.parentCount === 1 ? "" : "s"}`}</span> : null}
        </span>
        {n.count !== undefined && <Tag style={{ marginInlineEnd: 4 }} color="default">{n.count.toLocaleString()}</Tag>}
        {n.level === "note" ? <span style={{ flex: 1 }} /> : partAmounts(n)}
        {n.level === "derived" && onSelectParts ? (
          <Button
            size="small" type="text" loading={busyDerived === n.key}
            onClick={(e) => { e.stopPropagation(); selectParts(n); }}
            style={{ fontSize: 11, flexShrink: 0 }}
          >
            Select
          </Button>
        ) : <span style={{ width: canEdit ? 24 : 8, flexShrink: 0 }} />}
      </span>
    );
    const kids = derived
      .filter((n) => !filter || String(n.title).toLowerCase().includes(filter.toLowerCase()))
      .map((n) => ({ key: n.key, isLeaf: n.isLeaf, checkable: false, __node: n, title: rowTitle(n),
        children: n.children ? n.children.map((c) => ({ key: c.key, isLeaf: true, checkable: false, __node: c, title: rowTitle(c) })) : undefined }));
    if (!kids.length) return [];
    return [{
      key: "derivedRoot",
      checkable: false,
      isLeaf: false,
      __node: { key: "derivedRoot", level: "derivedRoot" },
      children: kids,
      title: (
        <span style={{ display: "flex", alignItems: "center", width: "100%", fontWeight: 600 }}>
          <span style={{ flex: 1 }}>Custom categories</span>
          <Tag color="default">{derived.length.toLocaleString()}</Tag>
        </span>
      ),
    }];
  }, [derived, filter, partAmounts, onSelectParts, busyDerived, selectParts, canEdit, derivedTicks, toggleDerived]);

  // Every key the tree has loaded, filter or no filter. The ant Tree
  // reports only the checked keys of nodes it knows: a saved pick under
  // a category nobody has expanded this visit is not in its table, and
  // the next tick anywhere came back without it (owner, 2026-09-14:
  // "checkboxes are not being saved"). The page merges what the tree
  // reports with what it could not know, using this set - see
  // scopeKeys.mergeCheckedKeys. The text filter hides rows from the ant
  // Tree, which forgets their keys too; a hidden row is still loaded.
  const loadedKeys = useMemo(() => {
    const out = new Set();
    const walk = (list) => list.forEach((n) => { out.add(String(n.key)); if (n.children) walk(n.children); });
    walk(nodes);
    return out;
  }, [nodes]);

  // ---- SIZES AND BOXES AS TOP ENTRIES ---------------------------------
  // Their own rows with their own boxes (checkable:false to the ant Tree),
  // because each one IS another node: a box entry is the Electrical
  // Fixtures type of that box, ticked by its real key; a conduit size has no
  // place in the category / family / type tree, so ticking it ticks its
  // elements - the conduits of that size in this model.
  const checkedSet = useMemo(() => new Set((checkedKeys || []).map(String)), [checkedKeys]);
  const loadSizeUids = useCallback(async () => {
    if (sizeUids) return sizeUids;
    setSizeBusy(true);
    try {
      const r = await getConduitSizeUids(projectId, { documentIds: docs });
      setSizeUids(r.sizes || {});
      return r.sizes || {};
    } catch (e) {
      message.error(e.message || "Could not read the conduit sizes");
      return null;
    } finally {
      setSizeBusy(false);
    }
  }, [sizeUids, projectId, docs]);
  // A row's tick: on, partly on, or covered by a ticked category / family.
  const shortcutState = useCallback((c) => {
    if (c.realKey) {
      const p = String(c.realKey).split(":").slice(1).join(":").split("|");
      const above = [`category:${p[0]}`, `family:${p[0]}|${p[1]}`].find((k) => checkedSet.has(k));
      if (above) return { on: true, by: above };
      return { on: checkedSet.has(String(c.realKey)) };
    }
    if (checkedSet.has("category:Conduits")) return { on: true, by: "category:Conduits" };
    const uids = sizeUids && sizeUids[c.sizeKey];
    if (!uids || !uids.length) return { on: false };
    const n = uids.filter((u) => checkedSet.has(`element:${u}`)).length;
    return { on: n === uids.length, some: n > 0 && n < uids.length };
  }, [checkedSet, sizeUids]);
  const toggleShortcut = useCallback(async (rows, on) => {
    if (!onSetChecked) return;
    const next = new Set(checkedSet);
    let map = sizeUids;
    if (rows.some((c) => c.sizeKey)) map = await loadSizeUids();
    for (const c of rows) {
      if (shortcutState(c).by) continue;            // its category is ticked; untick that
      const keys = c.realKey ? [String(c.realKey)] : ((map && map[c.sizeKey]) || []).map((u) => `element:${u}`);
      keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    }
    onSetChecked([...next]);
  }, [onSetChecked, checkedSet, sizeUids, loadSizeUids, shortcutState]);
  const shortcutTree = useMemo(() => {
    if (!shortcuts.length) return [];
    const box = (rows, label) => {
      const states = rows.map(shortcutState);
      const all = states.length > 0 && states.every((x) => x.on);
      const some = states.some((x) => x.on || x.some);
      const by = states.find((x) => x.by);
      const disabled = !canEdit || !onSetChecked || (all && states.every((x) => x.by));
      const cb = (
        <Checkbox
          checked={all} indeterminate={!all && some} disabled={disabled}
          onChange={(e) => { e.stopPropagation(); toggleShortcut(rows, e.target.checked); }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Track ${label}`}
          style={{ marginRight: 8, flexShrink: 0 }}
        />
      );
      return by && all
        ? <Tooltip getPopupContainer={popupInStage} title={`Included because "${String(by.by).split(":").slice(1).join(":").split("|").pop()}" is ticked below - untick it there first`}><span>{cb}</span></Tooltip>
        : cb;
    };
    const row = (label, count, rows, extra) => (
      <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
        {box(rows, label)}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}{extra ? <span style={{ color: T.muted, fontSize: 11 }}>{extra}</span> : null}
        </span>
        <Tag style={{ marginInlineEnd: 4 }} color="default">{Number(count || 0).toLocaleString()}</Tag>
      </span>
    );
    return shortcuts
      .filter((g) => !filter || String(g.label).toLowerCase().includes(filter.toLowerCase())
        || (g.children || []).some((c) => String(c.label).toLowerCase().includes(filter.toLowerCase())))
      .map((g) => ({
        key: g.key, checkable: false, isLeaf: false, __node: { key: g.key, level: "shortcut" },
        title: row(g.label, g.count, g.children, g.id === "conduitSizes" && sizeBusy ? " · reading the sizes…" : ""),
        children: (g.children || []).map((c) => ({
          key: c.key, checkable: false, isLeaf: true, __node: { key: c.key, level: "shortcutChild" },
          title: row(c.label, c.count, [c], c.family ? ` · ${c.family}` : ""),
        })),
      }));
  }, [shortcuts, filter, shortcutState, toggleShortcut, canEdit, onSetChecked, sizeBusy]);
  // The sizes' ticks need their element ids: read them as soon as the
  // entry is opened, so the boxes say what is ticked before anyone clicks.
  useEffect(() => {
    if (!sizeUids && expanded.includes("es:conduitSizes")) loadSizeUids();
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const treeData = useMemo(() => [...shortcutTree, ...antNodes, ...derivedTree], [shortcutTree, antNodes, derivedTree]);

  const onAntCheck = useCallback((keys, info) => {
    const list = Array.isArray(keys) ? keys : (keys && keys.checked) || [];
    if (onCheck) onCheck(list, info, loadedKeys);
  }, [onCheck, loadedKeys]);

  if (loading) return <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>;
  if (!nodes.length) {
    return <Empty description="No model elements. Publish a model from the plugin first." style={{ padding: 24 }} />;
  }

  return (
    <div>
      <Input
        size="small"
        allowClear
        prefix={<SearchOutlined style={{ color: T.muted }} />}
        placeholder="Filter what is loaded"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      {buildNote(quantities) && (
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6 }}>{buildNote(quantities)}</div>
      )}
      {/* Column heads for the amounts, aligned over the rows below. */}
      <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: canEdit ? 32 : 8, fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: 0.3 }}>
        {SUM_COLUMNS.map((col) => (
          <span key={col.key} style={{ width: COL_W, textAlign: "right", paddingRight: 4 }}>
            {col.label.toUpperCase()} <span style={{ fontWeight: 400 }}>{unitOf(col, units)}</span>
          </span>
        ))}
      </div>
      <div style={{ maxHeight: height, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 6, padding: 6 }}>
        <Tree
          blockNode
          checkable
          selectable={false}
          checkedKeys={checkedKeys}
          onCheck={onAntCheck}
          expandedKeys={expanded}
          onExpand={setExpanded}
          loadData={onLoadData}
          treeData={treeData}
          switcherIcon={<DownOutlined />}
        />
      </div>
      <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
        Tick the categories, families or types to track: the model colours them by status and the
        numbers count them. The dot moves one element on a step; the tick button on a section sets
        everything under it, whether or not it has been opened.
        {shortcuts.length ? (
          <>
            {" "}Conduits by size, Junction boxes and Pull boxes at the top are the same elements as in
            Conduits and Electrical Fixtures: a box ticked there is its type ticked below. A conduit
            size is ticked as the conduits of that size in this model - conduits a later export adds
            are not in it until the size is ticked again.
          </>
        ) : null}
        {derived.length ? (
          <>
            {" "}Custom categories count PARTS inside those elements, so they are listed separately
            and their numbers are not added to the totals above. Select one to pick its parts in the
            model. Ticking one includes it in the quantities table below; unticking leaves it
            listed here but out of that table. They cannot be given a status here: a part takes the
            status of the element it belongs to.
            {derivedCut ? " Parts carry no level of their own, so the levels this page is set to do not narrow these counts." : ""}
          </>
        ) : null}
      </div>
    </div>
  );
}
